import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment as env } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Per-operator admin UI prefs. Powers the sidenav unread badges on
 * Error Reports + Commerce Events:
 *
 *   - On app boot, GET /rest/admin/user-prefs/last-seen returns
 *     { last_seen: {error_reports, commerce_events},
 *       unread:   {error_reports, commerce_events} } — the unread
 *     counts are server-capped at 10 so the SPA renders 1..9 / 9+
 *     in a single round-trip without scanning hot collections.
 *
 *   - When the operator clicks the nav link, the destination panel
 *     snapshots last_seen for its arrival render, then this service
 *     POSTs /mark-seen which zeros the badge AND broadcasts via
 *     BroadcastChannel so sibling admin tabs same-origin also clear
 *     their badge instantly.
 *
 *   - Live-bump path: at start() the service opens BOTH the
 *     error-reports and commerce-events SSE streams globally (same
 *     endpoints the destination panels already use). Every new row
 *     with created_at > current last_seen bumps the badge locally
 *     within a beat — the badge grows in real time without waiting
 *     for the next poll. Bumping is suppressed when the operator is
 *     currently on that panel's route (they can see the row live,
 *     they don't need a badge to tell them one arrived).
 *
 *   - 20s background poll (was 60s) acts as safety net when SSE
 *     drops silently — reconnects periodically and re-syncs the count.
 */

export type LastSeenKind = 'error_reports' | 'commerce_events';

export interface LastSeenState {
  last_seen: { error_reports: string | null; commerce_events: string | null };
  unread: { error_reports: number; commerce_events: number };
}

const EMPTY_STATE: LastSeenState = {
  last_seen: { error_reports: null, commerce_events: null },
  unread: { error_reports: 0, commerce_events: 0 },
};

const BROADCAST_CHANNEL = 'f2-admin-user-prefs';
const POLL_MS = 20_000;
const SSE_RECONNECT_MS = 5_000;

// Kind → REST endpoint prefix (both endpoints follow /admin/<kind>/{stream-token,stream}).
const SSE_ENDPOINTS: Record<LastSeenKind, string> = {
  error_reports: 'admin/error-reports',
  commerce_events: 'admin/commerce-events',
};

// Kind → SPA route that renders that stream. Used to suppress
// live-bumps for the panel the operator is currently viewing.
const KIND_ROUTES: Record<LastSeenKind, string> = {
  error_reports: '/admin/error-reports',
  commerce_events: '/admin/commerce-events',
};

@Injectable({ providedIn: 'root' })
export class UserPrefsService {

  private _state$ = new BehaviorSubject<LastSeenState>(EMPTY_STATE);
  private _channel: BroadcastChannel | null = null;
  private _pollHandle: any = null;

  // Per-kind SSE plumbing: an EventSource per stream + a reconnect
  // timer that mints a fresh short-lived HMAC token then reopens.
  private _es: Record<LastSeenKind, EventSource | null> = {
    error_reports: null,
    commerce_events: null,
  };
  private _reconnectTimers: Record<LastSeenKind, any> = {
    error_reports: null,
    commerce_events: null,
  };

  // Snapshot taken at the moment the operator navigated to one of the
  // gated panels. The panel uses this (NOT the live last_seen, which
  // mark-seen has already bumped) to decide which rows render with the
  // NEW pill + customer-color tint.
  private _arrivalSnapshots: Record<LastSeenKind, string | null> = {
    error_reports: null,
    commerce_events: null,
  };

  // Current router URL — used to suppress the live-bump for whichever
  // panel is currently being viewed (they see the row directly). Kept
  // in sync via NavigationEnd subscription set up in start().
  private _currentUrl: string = '';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private zone: NgZone,
  ) {
    this._initBroadcastChannel();
    this._currentUrl = this.router.url || '';
  }

  state$(): Observable<LastSeenState> {
    return this._state$.asObservable();
  }

  snapshot(): LastSeenState {
    return this._state$.value;
  }

  /**
   * Called by AppComponent on bootstrap once the user is known to be
   * logged in. Pulls initial last_seen+unread and starts the 60s poll.
   * Safe to call multiple times — idempotent on the poll timer.
   */
  async start(): Promise<void> {
    if (!this.authService.is_logged_in()) return;
    // Track the active URL so SSE live-bumps can skip the panel the
    // operator is currently on.
    this._currentUrl = this.router.url || '';
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        this._currentUrl = e.urlAfterRedirects || e.url || '';
      });
    await this.refresh();
    this._startPolling();
    // Fire-and-forget — SSE bumps are a UX nicety, refresh() covers
    // the safety-net case if either stream fails to open.
    this._openStream('error_reports');
    this._openStream('commerce_events');
  }

  stop(): void {
    if (this._pollHandle) {
      clearInterval(this._pollHandle);
      this._pollHandle = null;
    }
    for (const kind of Object.keys(this._es) as LastSeenKind[]) {
      this._closeStream(kind);
      if (this._reconnectTimers[kind]) {
        clearTimeout(this._reconnectTimers[kind]);
        this._reconnectTimers[kind] = null;
      }
    }
  }

  async refresh(): Promise<void> {
    try {
      const res: any = await firstValueFrom(
        this.http.get(`${env.apiUrl}/rest/admin/user-prefs/last-seen`, { withCredentials: true })
      );
      if (res && res.last_seen && res.unread) {
        this._state$.next({
          last_seen: {
            error_reports: res.last_seen.error_reports || null,
            commerce_events: res.last_seen.commerce_events || null,
          },
          unread: {
            error_reports: Math.max(0, Math.floor(res.unread.error_reports || 0)),
            commerce_events: Math.max(0, Math.floor(res.unread.commerce_events || 0)),
          },
        });
      }
    } catch {
      // Silent — UserPrefs is a UI nicety, not a hard dependency. The
      // auth-401 interceptor handles auth failures globally.
    }
  }

  /**
   * Returns the arrival snapshot for `kind`, freezing it on first read
   * after the badge was non-zero — that's the value the destination
   * panel uses to decide which rows are "new since you last looked".
   * Calling beforeMarkSeen() right before markSeen() guarantees the
   * snapshot reflects the pre-bump state.
   */
  beforeMarkSeen(kind: LastSeenKind): void {
    this._arrivalSnapshots[kind] = this._state$.value.last_seen[kind];
  }

  arrivalSnapshot(kind: LastSeenKind): string | null {
    return this._arrivalSnapshots[kind];
  }

  /**
   * Bump last_seen for `kind` on the server, zero the local badge,
   * and broadcast to sibling tabs same-origin.
   */
  async markSeen(kind: LastSeenKind): Promise<void> {
    const atIso = new Date().toISOString();
    // Optimistic local update so the badge clears before the round-trip.
    const cur = this._state$.value;
    this._state$.next({
      last_seen: { ...cur.last_seen, [kind]: atIso },
      unread: { ...cur.unread, [kind]: 0 },
    });
    this._broadcast({ kind, at: atIso });
    try {
      await firstValueFrom(
        this.http.post(
          `${env.apiUrl}/rest/admin/user-prefs/last-seen/mark-seen`,
          { kind, at: atIso },
          { withCredentials: true },
        )
      );
    } catch {
      // Optimistic update already applied; a failed POST means the next
      // poll will surface accurate state again. Don't toast for a UI
      // nicety endpoint.
    }
  }

  /**
   * Live bump from inside a panel's SSE stream — when a new row lands
   * AND the operator is not currently on that panel, the side-nav badge
   * grows without waiting for the next poll. Capped to match server.
   */
  incrementLocal(kind: LastSeenKind): void {
    const cur = this._state$.value;
    const next = Math.min(10, cur.unread[kind] + 1);
    if (next === cur.unread[kind]) return;
    this._state$.next({
      ...cur,
      unread: { ...cur.unread, [kind]: next },
    });
  }

  private _broadcast(msg: { kind: LastSeenKind; at: string }) {
    try {
      this._channel?.postMessage({ type: 'mark-seen', ...msg });
    } catch {}
  }

  private _initBroadcastChannel() {
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      this._channel = new BroadcastChannel(BROADCAST_CHANNEL);
      this._channel.onmessage = (ev) => {
        const data = ev?.data;
        if (!data || data.type !== 'mark-seen') return;
        const kind = data.kind as LastSeenKind;
        if (kind !== 'error_reports' && kind !== 'commerce_events') return;
        const cur = this._state$.value;
        this._state$.next({
          last_seen: { ...cur.last_seen, [kind]: data.at || cur.last_seen[kind] },
          unread: { ...cur.unread, [kind]: 0 },
        });
      };
    } catch {}
  }

  private _startPolling() {
    if (this._pollHandle) return;
    this._pollHandle = setInterval(() => {
      if (this.authService.is_logged_in()) this.refresh();
    }, POLL_MS);
  }

  /**
   * Mint a short-lived HMAC bearer for the SSE stream (cookie-authed
   * POST) then open the EventSource with token on the query string.
   * Mirrors the pattern in ErrorReportsComponent._open_event_source
   * but held in this service so the badge stays live even when the
   * operator is on some other panel.
   */
  private async _openStream(kind: LastSeenKind): Promise<void> {
    if (this._es[kind]) return;
    if (this._reconnectTimers[kind]) return;
    if (!this.authService.is_logged_in()) return;

    let token: string;
    try {
      const mintUrl = `${env.apiUrl}/rest/${SSE_ENDPOINTS[kind]}/stream-token`;
      const res: any = await firstValueFrom(
        this.http.post(mintUrl, {}, { withCredentials: true })
      );
      token = res?.token;
      if (!token) throw new Error('mint response missing token');
    } catch (e: any) {
      // Silent — badge is a nicety, poll covers the gap.
      this._scheduleReconnect(kind);
      return;
    }

    try {
      const url = `${env.apiUrl}/rest/${SSE_ENDPOINTS[kind]}/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      this._es[kind] = es;
      es.onmessage = (ev: MessageEvent) => {
        if (!ev?.data) return;
        try {
          const doc = JSON.parse(ev.data);
          this.zone.run(() => this._onLiveDoc(kind, doc));
        } catch {
          /* malformed event; ignore */
        }
      };
      es.onerror = () => {
        // EventSource retries with the same (likely stale) token
        // forever — close + re-mint fresh instead.
        this._closeStream(kind);
        this._scheduleReconnect(kind);
      };
    } catch {
      this._closeStream(kind);
      this._scheduleReconnect(kind);
    }
  }

  private _closeStream(kind: LastSeenKind): void {
    const es = this._es[kind];
    if (!es) return;
    try { es.close(); } catch {}
    this._es[kind] = null;
  }

  private _scheduleReconnect(kind: LastSeenKind): void {
    if (this._reconnectTimers[kind]) return;
    this._reconnectTimers[kind] = setTimeout(() => {
      this._reconnectTimers[kind] = null;
      this._openStream(kind);
    }, SSE_RECONNECT_MS);
  }

  /**
   * SSE tick handler — bumps the local unread badge for `kind` when
   * the incoming row landed after our stored last_seen. Runs regardless
   * of which panel is active: Mike wants the badge to reflect new
   * arrivals in real time even while he's on the panel (2026-07-01),
   * so he can glance at the sidenav for a running count of "how much
   * has arrived since my last mark-seen" without visually parsing the
   * grid. Mark-seen (nav-click OR panel arrival) is still the sole
   * reset — after either, subsequent SSE rows grow the badge again.
   */
  private _onLiveDoc(kind: LastSeenKind, doc: any): void {
    const createdAt = doc?.created_at;
    if (!createdAt) return;
    const rowMs = Date.parse(String(createdAt));
    if (!Number.isFinite(rowMs)) return;
    const cur = this._state$.value;
    const seenIso = cur.last_seen[kind];
    const seenMs = seenIso ? Date.parse(seenIso) : 0;
    if (rowMs <= seenMs) return;
    this.incrementLocal(kind);
  }
}
